import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { AppConfig } from '../../utils/app-config.service';
import { IdInterface, OrderSignedInterface, OrderTypeAvailableInterface, SelectedProductInterface } from '../../../core';
import { LocalStorage } from '../helpers/local-storage';
import { MyHttpClient } from './my-http-client';
import { OrderTypeAvailable } from '../../domain/data';
import { Serializer } from '../helpers/serializer/serializer';

import { mapCommonResponse, mapOrderSigned } from './api-client.helpers';

import { environment } from '../../../../environments/environment';

@Injectable()
export class OrderApiClient {
    constructor(private http: MyHttpClient, private localStorage: LocalStorage, private serializer: Serializer) {}

    acceptOrderSignatures(selectedProductId: IdInterface): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.accept_order_signatures).pipe(
            switchMap((url) => {
                return this.http
                    .post(url, {
                        ListID: String(selectedProductId),
                    })
                    .pipe(map((resp: any) => mapCommonResponse(resp)));
            })
        );
    }

    addOrderSelectedProduct(product: SelectedProductInterface): Observable<OrderSignedInterface> {
        return of(AppConfig.settings.services.base + environment.services.paths.add_order_selected_product).pipe(
            switchMap((url) => {
                return this.http.post(url, this.serializer.toJson(product)).pipe(map((resp: any) => mapOrderSigned(resp)));
            })
        );
    }

    updateOrderSelectedProduct(product: SelectedProductInterface): Observable<OrderSignedInterface> {
        return of(AppConfig.settings.services.base + environment.services.paths.modify_order_selected_product).pipe(
            switchMap((url) => {
                return this.http.post(url, this.serializer.toJson(product)).pipe(map((resp: any) => mapOrderSigned(resp)));
            })
        );
    }

    removeOrderSelectedProduct(product: SelectedProductInterface): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.delete_order_selected_product).pipe(
            switchMap((url) => {
                const signatures = product.order.clients.map((client) => ({
                    ClientCode: String(client.clientCode),
                    SignatureType: String(client.signatureType),
                }));

                return this.http
                    .post(url, {
                        ListID: String(product.id),
                        ClientSignatures: signatures,
                    })
                    .pipe(map((resp: any) => mapCommonResponse(resp)));
            })
        );
    }

    continueOrderElectronicSignature(product: SelectedProductInterface, clientCode: IdInterface): Observable<OrderSignedInterface> {
        return of(AppConfig.settings.services.base + environment.services.paths.continue_order_electronic_signature).pipe(
            switchMap((url) => {
                return this.http
                    .post(url, {
                        Product: this.serializer.toJson(product),
                        ClientCode: String(clientCode),
                    })
                    .pipe(map((resp: any) => mapOrderSigned(resp)));
            })
        );
    }

    getOrderTypes(
        contractCode: IdInterface,
        product: SelectedProductInterface,
        clientCode?: IdInterface
    ): Observable<OrderTypeAvailableInterface[]> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_type_of_orders).pipe(
            switchMap((url) => {
                return this.http
                    .post(url, {
                        ContractCode: String(contractCode),
                        SelectedProduct: this.serializer.toJson(product),
                        ClientCode: String(clientCode),
                    })
                    .pipe(
                        map((resp: any) => {
                            localStorage.setItem('HiddenOperations', resp.hiddenOperations);
                            return this.serializer.fromJsonArray(resp.typeofOrders, OrderTypeAvailable);
                        })
                    );
            })
        );
    }

    rememberOrderElectronicSignature(product: SelectedProductInterface, clientCode: IdInterface): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.remember_order_electronic_signature).pipe(
            switchMap((url) => {
                return this.http
                    .post(url, {
                        Product: this.serializer.toJson(product),
                        ClientCode: String(clientCode),
                    })
                    .pipe(map((resp: any) => mapCommonResponse(resp)));
            })
        );
    }
}
